import {
  createContext, ReactElement, ReactNode, useContext, useEffect, useState,
} from 'react';
import { maskPhone } from 'utils/transforms';
import { IAddress, ZipInfo } from 'models/Address';
import { get } from 'utils/network';
import { AddressSignupDraft } from '../models/address.types';
import { useFetchAddresses } from '../services/address/address.queries';

type Props = {
  children: ReactNode
}

enum ComponentLengthType {
  STREET_NUMBER = 'short_name',
  ROUTE = 'long_name',
}

type ContextValue = {
  addresses: IAddress[];
  addressSignupDraft: AddressSignupDraft;
  setAddressSignupDraft: React.Dispatch<React.SetStateAction<AddressSignupDraft>>;
  resetAddressSignupDraft: () => void;
  refreshAddresses: () => Promise<void>;
  googleAutoComplete: (addressInputRef: React.MutableRefObject<HTMLDivElement | null>) => void;
  updateCityAndStateUsingZipcode: (zip: string) => Promise<void>;
};

const defaultAddressSignupDraft: AddressSignupDraft = {
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  zip: '',
  state: '',
  city: '',
  phone: maskPhone(''),
  deliveryInstructions: '',
  smsAccountAlerts: false,
  smsOffersAlerts: false,
};

const AddressContext = createContext<ContextValue | null>(null);

export function AddressProvider({ children }: Props): ReactElement {
  const { data: addresses = [], refetch } = useFetchAddresses();
  const [shippingAddress, setShippingAddress] = useState<IAddress>();

  const refreshAddresses = async () => {
    await refetch();
  };

  const [addressSignupDraft, setAddressSignupDraft] = useState<AddressSignupDraft>(
    defaultAddressSignupDraft,
  );

  const updateCityAndStateUsingZipcode = async (zip) => {
    if (zip.length === 5) {
      const response = await get<ZipInfo>(`/zip_codes/${zip}`);

      if (response.ok) {
        const code = response.parsedBody?.state_code;
        const cityName = response.parsedBody?.city;

        if (code && cityName) {
          setAddressSignupDraft((prev) => ({
            ...prev,
            state: code,
            city: cityName,
          }));
        }
      }
    }
  };

  const googleAutoComplete = (addressInputRef: React.MutableRefObject<HTMLDivElement
    | null>) => {
    if (window.google && addressInputRef.current) {
      const inputElement = document.getElementById(
        'add-address-address',
      ) as HTMLInputElement | null;

      if (!inputElement) {
        return;
      }

      const autoComplete = new window.google.maps.places.Autocomplete(
        (inputElement),
        {
          types: ['geocode'],
          componentRestrictions: { country: 'us' },
        },
      );

      autoComplete.addListener('place_changed', () => {
        const components = autoComplete.getPlace()?.address_components;
        if (components === undefined) return;

        const newAddress1 = [
          components[0][ComponentLengthType.STREET_NUMBER],
          components[1][ComponentLengthType.ROUTE],
        ].join(' ');
        setAddressSignupDraft((prev) => ({
          ...prev,
          address1: newAddress1,
          state: '',
          city: '',
          zip: '',
        }));

        let nextState = '';
        let nextCity = '';
        let nextZip = '';
        for (let i = 0; i < components.length; i++) {
          const addressType = components[i].types[0];
          const addressLengthType = ComponentLengthType[addressType.toUpperCase()]
            || 'short_name';
          if (addressType !== 'street_number' && addressType !== 'route') {
            const val = components[i][addressLengthType];
            if (addressType === 'administrative_area_level_1') {
              nextState = val;
            } else if (addressType === 'locality') {
              nextCity = val;
            } else if (addressType === 'postal_code') nextZip = val;
          }
        }
        setAddressSignupDraft((prev) => ({
          ...prev,
          state: nextState || prev.state,
          city: nextCity || prev.city,
          zip: nextZip || prev.zip,
        }));
      });
    }
  };

  const resetAddressSignupDraft = () => {
    setAddressSignupDraft(defaultAddressSignupDraft);
  };

  const contextValue: ContextValue = {
    addresses,
    addressSignupDraft,
    setAddressSignupDraft,
    resetAddressSignupDraft,
    refreshAddresses,
    googleAutoComplete,
    updateCityAndStateUsingZipcode,
  };

  return (
    <AddressContext.Provider value={contextValue}>
      {children}
    </AddressContext.Provider>
  );
}

export function useAddress():ContextValue {
  const context = useContext(AddressContext);
  if (!context) throw new Error('useAddress must be used within AddressContext');
  return context;
}
